import type {StateCreator} from 'zustand'
import type {PullRequestFileTreeDiff} from '../../page-data/payloads/file-tree'
import {produce} from 'immer'

export type DiffSummariesSlice = {
  diffSummaries: Record<string, PullRequestFileTreeDiff>
  summariesViewedStatus: Record<string, boolean>
  summariesViewedCount: number
  getDiffFromThreadID: (threadID: string) => PullRequestFileTreeDiff | undefined
  getPathDigestFromThreadID: (threadID: string) => string | undefined
  getPathDigestFromAnnotationID: (annotationID: number) => string | undefined
  getFilePathFromThreadID: (threadID: string) => string | undefined
  diffSummariesActions: {
    setDiffSummaries: (diffSummaries?: Record<string, PullRequestFileTreeDiff>) => void
    setDiffViewed: (path: string, viewed: boolean) => void
    onCommentThreadDeleted: ({path, threadID}: {path: string; threadID: string}) => void
    onCommentThreadAdded: ({
      path,
      diffLineKey,
      startPosition,
      threadID,
    }: {
      path: string
      diffLineKey: string
      startPosition: string | undefined
      threadID: string
    }) => void
  }
}

export interface DiffSummariesSliceOpts {
  initialDiffSummaries: Record<string, PullRequestFileTreeDiff>
  initialSummariesViewedStatus: Record<string, boolean>
  initialSummariesViewedCount: number
}

export const createDiffSummariesSlice =
  ({
    initialDiffSummaries,
    initialSummariesViewedStatus,
    initialSummariesViewedCount,
  }: DiffSummariesSliceOpts): StateCreator<DiffSummariesSlice, [['zustand/devtools', never]], [], DiffSummariesSlice> =>
  (set, get) => ({
    diffSummaries: initialDiffSummaries,
    summariesViewedCount: initialSummariesViewedCount,
    summariesViewedStatus: initialSummariesViewedStatus,
    codeowners: undefined,
    getDiffFromThreadID: threadID => {
      const diffSummaries = Object.values(get().diffSummaries)
      for (const diff of diffSummaries) {
        if (!diff.markersMap) continue
        for (const [_, marker] of Object.entries(diff.markersMap)) {
          const hasThread = marker.threads.some(thread => Number(thread.id) === Number(threadID))
          if (hasThread) {
            return diff
          }
        }
      }
      return undefined
    },
    getPathDigestFromThreadID: threadID => {
      const diff = get().getDiffFromThreadID(threadID)
      return diff ? diff.pathDigest : undefined
    },
    getPathDigestFromAnnotationID: annotationID => {
      const diffSummaries = Object.values(get().diffSummaries)
      for (const diff of diffSummaries) {
        if (!diff.markersMap) continue

        for (const [_, marker] of Object.entries(diff.markersMap)) {
          const hasAnnotation = marker.annotations.some(annotation => annotation.id === annotationID)
          if (hasAnnotation) {
            return diff.pathDigest
          }
        }
      }
      return undefined
    },
    getFilePathFromThreadID: threadID => {
      const diff = get().getDiffFromThreadID(threadID)
      return diff ? diff.path : undefined
    },
    diffSummariesActions: {
      setDiffSummaries: (diffSummaries?: Record<string, PullRequestFileTreeDiff>) => {
        set({diffSummaries}, undefined, 'updateDiffSummaries')
      },
      setDiffViewed: (path: string, viewed: boolean) => {
        set(state => {
          if (!state.diffSummaries) return state
          return {
            summariesViewedStatus: produce(state.summariesViewedStatus, m => {
              m[path] = viewed
            }),
            summariesViewedCount: state.summariesViewedCount + (viewed ? 1 : -1),
            diffSummaries: produce(state.diffSummaries, m => {
              if (m[path]) {
                m[path].markedAsViewed = viewed
              }
            }),
          }
        })
      },
      onCommentThreadAdded: ({
        path,
        diffLineKey,
        startPosition,
        threadID,
      }: {
        path: string
        diffLineKey: string
        startPosition: string | undefined
        threadID: string
      }) => {
        set(
          produce((state: DiffSummariesSlice) => {
            if (!state.diffSummaries) return

            const diff = state.diffSummaries[path]
            if (!diff) return

            const markersMap = diff.markersMap ?? {}
            markersMap[diffLineKey] ||= {
              threads: [],
              annotations: [],
            }
            const markersDiffLine = markersMap[diffLineKey]
            markersDiffLine.threads.push({
              id: parseInt(threadID),
              start: startPosition,
            })
            diff.totalCommentsCount = (diff.totalCommentsCount || 0) + 1
          }),
          undefined,
          'commentAddedForDiffSummary',
        )
      },
      onCommentThreadDeleted: ({path, threadID}: {path: string; threadID: string}) => {
        set(
          produce((state: DiffSummariesSlice) => {
            if (!state.diffSummaries) return

            const diff = state.diffSummaries[path]
            if (!diff) return
            if (!diff?.totalCommentsCount || !diff?.markersMap) return

            for (const [markerID, marker] of Object.entries(diff.markersMap)) {
              marker.threads = marker.threads.filter(thread => thread.id !== parseInt(threadID))

              if (marker.threads.length === 0 && marker.annotations.length === 0) {
                delete diff.markersMap[markerID]
              }
            }
          }),
          undefined,
          'commentDeletedForDiffSummary',
        )
      },
    },
  })
